import { Extension, getMarkAttributes, getMarkType } from "@tiptap/core";

export interface IFontSizeOptions {
    sizes: Record<string, number>;
    HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Commands<ReturnType> {
        fontSize: {
            /**
             * Set the font size
             */
            setFontSize: (fontSizeName: string) => ReturnType;
            /**
             * Unset the font size
             */
            unsetFontSize: () => ReturnType;
        };
    }
}

export const FontSizeExtension = Extension.create<IFontSizeOptions>({
    name: "fontSize",

    addGlobalAttributes() {
        return [
            {
                types: ["textStyle"],
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: (element) => {
                            const rawFontSize = element.style.fontSize;
                            const supportedFontSizes = this.options.sizes;
                            const supportedFontSizesReverseMap = Object.entries(
                                supportedFontSizes,
                            ).reduce<Record<string, string | undefined>>(
                                (reverseMap, [fontSizeName, fontSize]) => {
                                    reverseMap[String(fontSize) + "em"] = fontSizeName;
                                    return reverseMap;
                                },
                                {},
                            );

                            return supportedFontSizesReverseMap[rawFontSize];
                        },
                        renderHTML: (attributes) => {
                            if (!attributes.fontSize) {
                                return {};
                            }
                            const fontSize = this.options.sizes[attributes.fontSize];

                            // 1 em doesn't have any effect anyways, so let's not render the attribute
                            if (fontSize === 1) {
                                return {};
                            }

                            return {
                                style: `font-size: ${fontSize}em`,
                            };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setFontSize:
                (fontSize) =>
                ({ chain, can, dispatch }) => {
                    if (dispatch) {
                        return chain().setMark("textStyle", { fontSize }).run();
                    }
                    return can().chain().setMark("textStyle", { fontSize }).run();
                },
            unsetFontSize:
                () =>
                ({ can, chain, dispatch, tr, state }) => {
                    if (dispatch) {
                        const { selection } = tr;
                        const { empty } = selection;
                        const type = getMarkType("textStyle", state.schema);

                        if (empty) {
                            // This is really ugly. Should be able to remove this when https://github.com/ueberdosis/tiptap/issues/3702 is resolved
                            return chain()
                                .command(({ tr: subCmdTr, state, commands }) => {
                                    const oldAttributes = getMarkAttributes(state, type);

                                    subCmdTr.addStoredMark(
                                        type.create({
                                            ...oldAttributes,
                                            ...{ fontSize: null },
                                        }),
                                    );

                                    const newAttributes = getMarkAttributes(
                                        state.apply(subCmdTr),
                                        type,
                                    );
                                    const hasStyles = Object.entries(newAttributes).some(
                                        ([, value]) => !!value,
                                    );

                                    if (hasStyles) {
                                        return true;
                                    }

                                    return commands.unsetMark("textStyle");
                                })
                                .run();
                        } else {
                            return chain()
                                .setMark("textStyle", { fontSize: null })
                                .removeEmptyTextStyle()
                                .run();
                        }
                    }
                    return can()
                        .chain()
                        .setMark("textStyle", { fontSize: null })
                        .resetAttributes("textStyle", "fontSize")
                        .removeEmptyTextStyle()
                        .run();
                },
        };
    },
});

export default FontSizeExtension;
